import { useEffect, useRef, useState } from 'react';

import { STOP_WITHOUT_ERROR } from '#src/components/Form/ControlledForm.tsx';
import type { Data } from '#src/components/Form/index.tsx';
import { Solana } from '#src/components/icons/Solana.tsx';
import useInfo from '#src/hooks/useInfo.ts';
import useLoadWallet from '#src/hooks/useLoadWallet.ts';
import useReturnToWalletSettingsTab from '#src/hooks/useReturnToWalletSettingsTab.ts';
import { DecryptionError, KeyFetchError } from '#src/lib/errors/index.ts';
import { isMobileDevice } from '#src/lib/isMobileDevice.ts';

import WalletIsLocked from './WalletIsLocked.tsx';
import type { Props } from './types.ts';

interface Keys {
  public: string;
  private: string;
}

type CopyStatus = 'idle' | 'copied' | 'failed';

const COPY_ICONS: Record<CopyStatus, string> = {
  idle: '📋',
  copied: '✅',
  failed: '⚠️',
};

const PrivateKey = ({ onSuccess }: Props) => {
  const loadWallet = useLoadWallet();
  const { dict } = useInfo();
  const [solanaKeys, setSolanaKeys] = useState<Keys | null>(null);
  const [showSolanaPrivateKey, setShowSolanaPrivateKey] =
    useState<boolean>(false);
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');
  const [exportUrl, setExportUrl] = useState<string | null>(null);
  const privateKeyRef = useRef<HTMLSpanElement>(null);

  useEffect(
    () => () => {
      if (exportUrl) URL.revokeObjectURL(exportUrl);
    },
    [exportUrl]
  );

  useEffect(() => {
    if (copyStatus === 'idle') return undefined;
    const timeout = setTimeout(() => setCopyStatus('idle'), 2000);
    return () => clearTimeout(timeout);
  }, [copyStatus]);

  const copyPrivateKey = async () => {
    if (!solanaKeys) return;
    try {
      await navigator.clipboard.writeText(solanaKeys.private);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
      if (privateKeyRef.current)
        window.getSelection()?.selectAllChildren(privateKeyRef.current);
    }
  };

  const returnToWalletSettingsTab = useReturnToWalletSettingsTab();

  const close = () => {
    returnToWalletSettingsTab();
    setTimeout(onSuccess, 50);
  };

  const onSubmit = async ({ currentPassword }: Data<string>) => {
    try {
      const wallet = await loadWallet({ password: currentPassword });
      if (wallet) {
        setSolanaKeys({
          public: wallet.solanaWallet.address,
          private: wallet.solanaWallet.privateKey,
        });
        if (!isMobileDevice()) {
          setExportUrl(
            URL.createObjectURL(
              new Blob(
                [
                  JSON.stringify(
                    Array.from(wallet.solanaWallet.privateKeyBytes)
                  ),
                ],
                { type: 'application/json' }
              )
            )
          );
        }
      }
      return undefined;
    } catch (e) {
      if (e instanceof DecryptionError)
        return { currentPassword: dict.passwordIsInvalid };
      if (e instanceof KeyFetchError) return STOP_WITHOUT_ERROR;
      throw e;
    }
  };

  if (solanaKeys?.private) {
    return (
      <div className="scroll-dialog wallet-drawer">
        <div className="warning">{dict.solanaPrivateKeyWarning}</div>
        <span className="group-label">
          <Solana />
          {dict.solanaKeysTitle}
        </span>
        <div className="helper">{dict.solanaKeysComingSoon}</div>
        <div className="private-keys">
          <div className="private-key">
            <span className="private-key-label">
              {dict.solanaPublicAddress}
            </span>
            <span className="private-key-value">{solanaKeys?.public}</span>
          </div>
          <div className="private-key">
            <span className="private-key-label">
              {dict.solanaPrivateKey}
              <span>
                <button
                  type="submit"
                  className="stroke small icon"
                  onClick={() => {
                    setShowSolanaPrivateKey(v => !v);
                    setCopyStatus('idle');
                    if (showSolanaPrivateKey)
                      window.getSelection()?.removeAllRanges();
                  }}
                >
                  {showSolanaPrivateKey ? '🔐' : '🔓'}
                </button>
                {!!showSolanaPrivateKey && (
                  <button
                    type="submit"
                    className="stroke small icon"
                    aria-label={dict.copySolanaPrivateKey}
                    title={dict.copySolanaPrivateKey}
                    onClick={copyPrivateKey}
                  >
                    {COPY_ICONS[copyStatus]}
                  </button>
                )}
              </span>
            </span>
            <span className="private-key-value" ref={privateKeyRef}>
              {showSolanaPrivateKey ? solanaKeys?.private : '*'.repeat(66)}
            </span>
          </div>
        </div>
        {!!exportUrl && (
          <a
            href={exportUrl}
            download="solana-keys.json"
            style={{ flexShrink: 0 }}
            className="export-button"
          >
            {dict.exportSolanaPrivateKey}
          </a>
        )}

        <button
          onClick={close}
          type="submit"
          className="stroke"
          style={{ flexShrink: 0 }}
        >
          {dict.close}
        </button>
      </div>
    );
  }
  return <WalletIsLocked onSubmit={onSubmit} />;
};
export default PrivateKey;
